import { useState, useEffect } from "react";
import { useForm } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useBasePath } from "@/lib/paths";
import { FormationSupports, PrincipalRoles, DocumentAttachment } from "@/types";
import { CheckCircle2 } from "lucide-react";
import { LocationSelector } from "@/components/ui/LocationSelector";
import { DocumentUploader } from "@/components/shared/DocumentUploader";
import { InternationalPhoneInput } from "@/components/ui/international-phone-input";

type LocationItem = { id: string; code: string; name: string };
type StateItem = LocationItem & { country_id: string };
type LocalAreaItem = LocationItem & { state_id: string };

type Principal = {
  id: string;
  full_name: string;
  legal_name?: string;
  email?: string | null;
  phone?: string;
  principal_kind: string;
  country_code: string;
  state_province?: string;
  city?: string;
  postal_code?: string;
  street_address_1?: string;
  street_address_2?: string;
  date_of_birth?: string;
  tax_id_last4?: string;
  kyc_verified?: boolean;
  roles: PrincipalRoles;
  documents: DocumentAttachment[];
};

type Props = {
  formationId: string;
  formationName: string;
  supports: FormationSupports;
  principal: Principal | null;
  countries: LocationItem[];
  states: StateItem[];
  localAreas: LocalAreaItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  basePath?: string;
};

const ROLE_LABELS: Record<string, string> = {
  owners: "Owner",
  directors: "Director",
  president: "President",
  secretary: "Secretary",
  treasurer: "Treasurer",
  managers: "Manager",
  registered_agent: "Registered agent",
};

const ROLE_PARAM_KEY: Record<string, string> = {
  owners: "owner",
  directors: "director",
  president: "president",
  secretary: "secretary",
  treasurer: "treasurer",
  managers: "manager",
  registered_agent: "registered_agent",
};

function rolesFromPrincipal(
  roles: PrincipalRoles | undefined,
): Record<string, any> {
  if (!roles) return {};
  const result: Record<string, any> = {};
  if (roles.owner)
    result.owner = {
      ownership_percentage: roles.owner.ownership_percentage ?? "",
      capital_contribution_cents: roles.owner.capital_contribution_cents ?? "",
      voting_rights: roles.owner.voting_rights ? "true" : "false",
    };
  if (roles.director)
    result.director = {
      board_chair: roles.director.board_chair ? "true" : "false",
    };
  if (roles.president) result.president = {};
  if (roles.secretary) result.secretary = {};
  if (roles.treasurer) result.treasurer = {};
  if (roles.manager)
    result.manager = {
      management_authority: roles.manager.management_authority || "full",
    };
  if (roles.incorporator) result.incorporator = {};
  if (roles.registered_agent) result.registered_agent = {};
  return result;
}

function selectedRolesFromPrincipal(
  roles: PrincipalRoles | undefined,
): Set<string> {
  if (!roles) return new Set();
  const set = new Set<string>();
  // Map param keys back to supports keys
  const reverseMap: Record<string, string> = {
    owner: "owners",
    director: "directors",
    president: "president",
    secretary: "secretary",
    treasurer: "treasurer",
    manager: "managers",
    incorporator: "incorporator",
    registered_agent: "registered_agent",
  };
  for (const key of Object.keys(roles)) {
    if (reverseMap[key]) set.add(reverseMap[key]);
  }
  return set;
}

export function EditPrincipalDialog({
  formationId,
  formationName,
  supports,
  principal,
  countries,
  states,
  localAreas,
  open,
  onOpenChange,
  basePath = "",
}: Props) {
  const defaultBasePath = useBasePath();
  const actualBasePath = basePath || defaultBasePath;
  const [success, setSuccess] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());

  const form = useForm<Record<string, any>>({
    principal_kind: principal?.principal_kind || "individual",
    full_name: principal?.full_name || "",
    legal_name: principal?.legal_name || "",
    email: principal?.email || "",
    phone: principal?.phone || "",
    country_code: principal?.country_code || "",
    state_province: principal?.state_province || "",
    city: principal?.city || "",
    postal_code: principal?.postal_code || "",
    street_address_1: principal?.street_address_1 || "",
    street_address_2: principal?.street_address_2 || "",
    date_of_birth: principal?.date_of_birth || "",
    tax_id_last4: principal?.tax_id_last4 || "",
    roles: rolesFromPrincipal(principal?.roles),
  });

  useEffect(() => {
    if (principal && open) {
      form.setData({
        principal_kind: principal.principal_kind || "individual",
        full_name: principal.full_name || "",
        legal_name: principal.legal_name || "",
        email: principal.email || "",
        phone: principal.phone || "",
        country_code: principal.country_code || "",
        state_province: principal.state_province || "",
        city: principal.city || "",
        postal_code: principal.postal_code || "",
        street_address_1: principal.street_address_1 || "",
        street_address_2: principal.street_address_2 || "",
        date_of_birth: principal.date_of_birth || "",
        tax_id_last4: principal.tax_id_last4 || "",
        roles: rolesFromPrincipal(principal.roles),
      });
      setSelectedRoles(selectedRolesFromPrincipal(principal.roles));
    }
  }, [principal?.id, open]);

  const supportedRoles = Object.entries(supports)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const toggleRole = (roleKey: string) => {
    const next = new Set(selectedRoles);
    const paramKey = ROLE_PARAM_KEY[roleKey];
    if (next.has(roleKey)) {
      next.delete(roleKey);
      const roles = { ...form.data.roles };
      delete roles[paramKey];
      form.setData("roles", roles);
    } else {
      next.add(roleKey);
      const defaults: Record<string, any> = {};
      if (paramKey === "owner") defaults.ownership_percentage = "";
      if (paramKey === "director") defaults.board_chair = "false";
      if (paramKey === "manager") defaults.management_authority = "full";
      form.setData("roles", { ...form.data.roles, [paramKey]: defaults });
    }
    setSelectedRoles(next);
  };

  const updateRoleField = (paramKey: string, field: string, value: string) => {
    form.setData("roles", {
      ...form.data.roles,
      [paramKey]: { ...form.data.roles[paramKey], [field]: value },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!principal) return;
    form.patch(
      `${actualBasePath}/formations/${formationId}/principals/${principal.id}`,
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => {
            setSuccess(false);
            onOpenChange(false);
          }, 1000);
        },
      },
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.clearErrors();
      setSuccess(false);
    }
    onOpenChange(open);
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              Person updated
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{principal?.full_name || "Edit Person"}</DialogTitle>
          <DialogDescription>
            Update details for {formationName}
          </DialogDescription>
        </DialogHeader>

        <form
          id="person-form"
          onSubmit={handleSubmit}
          className="space-y-5 px-4"
        >
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Person type
            </Label>
            <RadioGroup
              value={form.data.principal_kind}
              onValueChange={(value) => form.setData("principal_kind", value)}
              className="flex gap-4"
              aria-label="Person type"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="individual" id="edit-individual" />
                <Label
                  htmlFor="edit-individual"
                  className="text-sm font-normal"
                >
                  Individual
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="entity" id="edit-entity" />
                <Label htmlFor="edit-entity" className="text-sm font-normal">
                  Entity
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Roles */}
          {supportedRoles.length > 0 && (
            <div>
              <Label className="text-sm font-medium mb-2 block">Roles</Label>
              <div className="space-y-2">
                {supportedRoles.map((roleKey) => (
                  <div key={roleKey}>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-role-${roleKey}`}
                        checked={selectedRoles.has(roleKey)}
                        onCheckedChange={() => toggleRole(roleKey)}
                      />
                      <Label
                        htmlFor={`edit-role-${roleKey}`}
                        className="text-sm font-normal"
                      >
                        {ROLE_LABELS[roleKey]}
                      </Label>
                    </div>
                    {selectedRoles.has(roleKey) &&
                      ROLE_PARAM_KEY[roleKey] === "owner" && (
                        <div className="ml-6 mt-2 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label
                                htmlFor="edit-ownership-percentage"
                                className="text-xs text-muted-foreground"
                              >
                                Ownership %
                              </Label>
                              <Input
                                id="edit-ownership-percentage"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={
                                  form.data.roles?.owner
                                    ?.ownership_percentage ?? ""
                                }
                                onChange={(e) =>
                                  updateRoleField(
                                    "owner",
                                    "ownership_percentage",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g. 50"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label
                                htmlFor="edit-capital-contribution"
                                className="text-xs text-muted-foreground"
                              >
                                Capital ($)
                              </Label>
                              <Input
                                id="edit-capital-contribution"
                                type="number"
                                min="0"
                                value={
                                  form.data.roles?.owner
                                    ?.capital_contribution_cents ?? ""
                                }
                                onChange={(e) =>
                                  updateRoleField(
                                    "owner",
                                    "capital_contribution_cents",
                                    e.target.value,
                                  )
                                }
                                placeholder="In cents"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    {selectedRoles.has(roleKey) &&
                      ROLE_PARAM_KEY[roleKey] === "director" && (
                        <div className="ml-6 mt-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="edit-board-chair"
                              checked={
                                form.data.roles?.director?.board_chair ===
                                "true"
                              }
                              onCheckedChange={(checked) =>
                                updateRoleField(
                                  "director",
                                  "board_chair",
                                  checked ? "true" : "false",
                                )
                              }
                            />
                            <Label
                              htmlFor="edit-board-chair"
                              className="text-xs font-normal text-muted-foreground"
                            >
                              Board Chair
                            </Label>
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-medium">Name</Label>
            <div className="space-y-1.5">
              <Input
                value={form.data.full_name}
                onChange={(e) => form.setData("full_name", e.target.value)}
                placeholder={
                  form.data.principal_kind === "entity"
                    ? "Entity legal name"
                    : "Full name"
                }
                aria-label={
                  form.data.principal_kind === "entity"
                    ? "Entity legal name"
                    : "Full name"
                }
                required
              />
              {form.errors.full_name && (
                <p className="text-[12px] text-destructive">
                  {form.errors.full_name}
                </p>
              )}
            </div>
            {form.data.principal_kind === "entity" && (
              <Input
                value={form.data.legal_name}
                onChange={(e) => form.setData("legal_name", e.target.value)}
                placeholder="DBA name (optional)"
                aria-label="DBA name (optional)"
              />
            )}
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Contact</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Input
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  placeholder="Email"
                  aria-label="Email"
                />
                {form.errors.email && (
                  <p className="text-[12px] text-destructive">
                    {form.errors.email}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <InternationalPhoneInput
                  id="edit-principal-phone"
                  value={form.data.phone}
                  onValueChange={(phone) => form.setData("phone", phone)}
                  defaultCountryCode={form.data.country_code}
                  placeholder="Phone"
                  ariaLabel="Phone"
                  ariaInvalid={!!form.errors.phone}
                  ariaDescribedBy={form.errors.phone ? "edit-principal-phone-error" : undefined}
                />
                {form.errors.phone && (
                  <p id="edit-principal-phone-error" className="text-[12px] text-destructive">
                    {form.errors.phone}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Address</Label>
            <Input
              value={form.data.street_address_1}
              onChange={(e) => form.setData("street_address_1", e.target.value)}
              placeholder="Street address"
              aria-label="Street address"
            />
            <Input
              value={form.data.street_address_2}
              onChange={(e) => form.setData("street_address_2", e.target.value)}
              placeholder="Apt, suite, etc. (optional)"
              aria-label="Apartment, suite, etc. (optional)"
            />
            <LocationSelector
              countries={countries}
              states={states}
              localAreas={localAreas}
              countryCode={form.data.country_code}
              stateProvince={form.data.state_province}
              city={form.data.city}
              onChange={({ country_code, state_province, city }) => {
                form.setData((d: typeof form.data) => ({
                  ...d,
                  country_code,
                  state_province,
                  city,
                }));
              }}
            />
            <Input
              value={form.data.postal_code}
              onChange={(e) => form.setData("postal_code", e.target.value)}
              placeholder="ZIP code"
              aria-label="ZIP code"
              className="h-8 text-sm"
            />
          </div>

          {form.data.principal_kind === "individual" && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Personal information
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="edit-date-of-birth"
                    className="text-xs text-muted-foreground"
                  >
                    Date of Birth
                  </Label>
                  <Input
                    id="edit-date-of-birth"
                    type="date"
                    value={form.data.date_of_birth}
                    onChange={(e) =>
                      form.setData("date_of_birth", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="edit-tax-id-last4"
                    className="text-xs text-muted-foreground"
                  >
                    SSN Last 4
                  </Label>
                  <Input
                    id="edit-tax-id-last4"
                    maxLength={4}
                    value={form.data.tax_id_last4}
                    onChange={(e) =>
                      form.setData("tax_id_last4", e.target.value)
                    }
                    placeholder="1234"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="person-form"
                className="flex-1"
                disabled={form.processing}
              >
                {form.processing ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </form>

        {/* KYC documents — outside the form to avoid nested form HTML violation */}
        {principal && (
          <div className="border-t pt-4 mt-2 px-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              KYC documents
            </p>
            <DocumentUploader
              title="KYC documents"
              documents={principal.documents || []}
              uploadPath={`${actualBasePath}/formations/${formationId}/principals/${principal.id}/documents`}
              deletePath={(docId) =>
                `${actualBasePath}/formations/${formationId}/principals/${principal.id}/documents/${docId}`
              }
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
